import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styled, { keyframes } from 'styled-components';
import { useTheme } from '../contexts/ThemeContext';

const floatAnimation = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-3px) rotate(2deg); }
`;

const glowAnimation = keyframes`
  0%, 100% { box-shadow: 0 0 20px rgba(100, 149, 237, 0.3); }
  50% { box-shadow: 0 0 30px rgba(100, 149, 237, 0.6); }
`;

const ToggleButton = styled(motion.button).withConfig({
  shouldForwardProp: (prop) => !['isDarkMode'].includes(prop),
})`
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: linear-gradient(145deg, #2c3e50 0%, #34495e 30%, #4a5f7f 70%, #5a6f8f 100%);
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: white;
  font-size: 22px;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(20px);
  box-shadow: 
    0 8px 32px rgba(44, 62, 80, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.2),
    inset 0 -1px 0 rgba(0, 0, 0, 0.1);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, transparent 60%);
    opacity: 0;
    transition: opacity 0.4s ease;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 120%;
    height: 120%;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.25) 0%, transparent 50%);
    transform: translate(-50%, -50%) scale(0);
    transition: transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  
  &:hover {
    transform: scale(1.08) translateY(-3px);
    box-shadow: 
      0 12px 40px rgba(44, 62, 80, 0.4),
      0 4px 12px rgba(0, 0, 0, 0.15),
      inset 0 1px 0 rgba(255, 255, 255, 0.3);
    border-color: rgba(255, 255, 255, 0.25);
    background: linear-gradient(145deg, #34495e 0%, #4a5f7f 30%, #5a6f8f 70%, #6a7f9f 100%);
    
    &::before {
      opacity: 1;
    }
    
    &::after {
      transform: translate(-50%, -50%) scale(1);
      opacity: 0;
    }
  }
  
  &:active {
    transform: scale(1.02) translateY(-1px);
    box-shadow: 
      0 6px 20px rgba(44, 62, 80, 0.3),
      inset 0 1px 0 rgba(255, 255, 255, 0.25);
  }
  
  @media (max-width: 768px) {
    width: 46px;
    height: 46px;
    font-size: 20px;
  }
  
  @media (max-width: 480px) {
    width: 42px;
    height: 42px;
    font-size: 18px;
  }
`;

const IconWrapper = styled(motion.div)`
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ThemeIcon = styled(motion.div)`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: inherit;
`;

const ThemeToggle = () => {
  const { isDarkMode, toggleTheme } = useTheme();

  return (
    <ToggleButton
      isDarkMode={isDarkMode}
      onClick={toggleTheme}
      whileHover={{ 
        scale: 1.1,
        rotate: [0, 5, -5, 0],
        transition: { duration: 0.3 }
      }}
      whileTap={{ 
        scale: 0.95,
        rotate: [0, -2, 0],
        transition: { duration: 0.1 }
      }}
      initial={{ opacity: 0, scale: 0 }}
      animate={{ 
        opacity: 1, 
        scale: 1,
        transition: { 
          duration: 0.5, 
          delay: 0.3,
          type: "spring",
          stiffness: 200
        }
      }}
      title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
      aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
    >
      <IconWrapper>
        <AnimatePresence mode="wait">
          {isDarkMode ? (
            <ThemeIcon
              key="moon"
              initial={{ rotate: -90, opacity: 0, scale: 0.5 }}
              animate={{ rotate: 0, opacity: 1, scale: 1 }}
              exit={{ rotate: 90, opacity: 0, scale: 0.5 }}
              transition={{ 
                duration: 0.4, 
                ease: [0.4, 0, 0.2, 1],
                scale: { duration: 0.3 }
              }}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <linearGradient id="moonGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stop-color="white" stop-opacity="1"/>
                    <stop offset="100%" stop-color="white" stop-opacity="0.9"/>
                  </linearGradient>
                  <mask id="crescentMask">
                    <rect x="0" y="0" width="24" height="24" fill="white"/>
                    <circle cx="14" cy="12" r="5.5" fill="black"/>
                  </mask>
                  <filter id="sharpen">
                    <feGaussianBlur stdDeviation="0.1"/>
                  </filter>
                </defs>
                
                <circle cx="12" cy="12" r="9" stroke="url(#moonGradient)" stroke-width="0.8" fill="none" opacity="0.8"/>
                
                <circle cx="12" cy="12" r="6" fill="url(#moonGradient)" mask="url(#crescentMask)" filter="url(#sharpen)"/>
                
                <circle cx="4" cy="4" r="0.6" fill="url(#moonGradient)" opacity="0.8"/>
                <circle cx="20" cy="5" r="0.5" fill="url(#moonGradient)" opacity="0.8"/>
                <circle cx="19" cy="19" r="0.6" fill="url(#moonGradient)" opacity="0.8"/>
                <circle cx="5" cy="20" r="0.4" fill="url(#moonGradient)" opacity="0.8"/>
                
                <circle cx="8" cy="9" r="1" fill="white" opacity="0.4"/>
              </svg>
            </ThemeIcon>
          ) : (
            <ThemeIcon
              key="sun"
              initial={{ rotate: 90, opacity: 0, scale: 0.5 }}
              animate={{ rotate: 0, opacity: 1, scale: 1 }}
              exit={{ rotate: -90, opacity: 0, scale: 0.5 }}
              transition={{ 
                duration: 0.4, 
                ease: [0.4, 0, 0.2, 1],
                scale: { duration: 0.3 }
              }}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <linearGradient id="sunGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stop-color="#ffd700" stop-opacity="0.95"/>
                    <stop offset="50%" stop-color="#ffed4e" stop-opacity="0.9"/>
                    <stop offset="100%" stop-color="white" stop-opacity="0.85"/>
                  </linearGradient>
                </defs>
                
                <circle cx="12" cy="12" r="4.5" fill="url(#sunGradient)" opacity="0.95"/>
                <circle cx="12" cy="12" r="6.5" stroke="url(#sunGradient)" stroke-width="1" fill="none" opacity="0.6"/>
                
                <g stroke="url(#sunGradient)" stroke-width="1.5" fill="none" opacity="0.8">
                  <path d="M12 2L12 5" stroke-linecap="round"/>
                  <path d="M12 19L12 22" stroke-linecap="round"/>
                  <path d="M4.22 4.22L6.34 6.34" stroke-linecap="round"/>
                  <path d="M17.66 17.66L19.78 19.78" stroke-linecap="round"/>
                  <path d="M2 12L5 12" stroke-linecap="round"/>
                  <path d="M19 12L22 12" stroke-linecap="round"/>
                  <path d="M4.22 19.78L6.34 17.66" stroke-linecap="round"/>
                  <path d="M17.66 6.34L19.78 4.22" stroke-linecap="round"/>
                </g>
                
                <g stroke="url(#sunGradient)" stroke-width="0.5" fill="none" opacity="0.4">
                  <path d="M7.5 7.5L8.5 8.5" stroke-linecap="round"/>
                  <path d="M15.5 15.5L16.5 16.5" stroke-linecap="round"/>
                  <path d="M7.5 16.5L8.5 15.5" stroke-linecap="round"/>
                  <path d="M15.5 8.5L16.5 7.5" stroke-linecap="round"/>
                </g>
                
                <circle cx="12" cy="12" r="2.5" fill="white" opacity="0.8"/>
              </svg>
            </ThemeIcon>
          )}
        </AnimatePresence>
      </IconWrapper>
    </ToggleButton>
  );
};

export default ThemeToggle;
